// Uniform-grid spatial hash over entities with x, y, r.
// Rebuilt once per tick on the server; queried by the module (world.query) and by
// per-client viewport culling. Results are returned sorted by id so module code that
// iterates them stays deterministic.

export interface Spatial {
  id: number;
  x: number;
  y: number;
  r: number;
}

export class SpatialHash<T extends Spatial> {
  private cells = new Map<number, T[]>();
  readonly cellSize: number;

  constructor(cellSize = 256) {
    this.cellSize = cellSize;
  }

  clear(): void {
    this.cells.clear();
  }

  rebuild(items: Iterable<T>): void {
    this.clear();
    for (const it of items) this.insert(it);
  }

  insert(it: T): void {
    const cs = this.cellSize;
    const x0 = Math.floor((it.x - it.r) / cs);
    const x1 = Math.floor((it.x + it.r) / cs);
    const y0 = Math.floor((it.y - it.r) / cs);
    const y1 = Math.floor((it.y + it.r) / cs);
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        const k = key(cx, cy);
        let bucket = this.cells.get(k);
        if (!bucket) {
          bucket = [];
          this.cells.set(k, bucket);
        }
        bucket.push(it);
      }
    }
  }

  /** Everything whose bounding circle overlaps the axis-aligned rectangle. */
  queryRect(x0: number, y0: number, x1: number, y1: number): T[] {
    const cs = this.cellSize;
    const cx0 = Math.floor(x0 / cs);
    const cx1 = Math.floor(x1 / cs);
    const cy0 = Math.floor(y0 / cs);
    const cy1 = Math.floor(y1 / cs);
    const seen = new Set<number>();
    const out: T[] = [];
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const bucket = this.cells.get(key(cx, cy));
        if (!bucket) continue;
        for (const it of bucket) {
          if (seen.has(it.id)) continue;
          if (it.x + it.r < x0 || it.x - it.r > x1 || it.y + it.r < y0 || it.y - it.r > y1) continue;
          seen.add(it.id);
          out.push(it);
        }
      }
    }
    out.sort((a, b) => a.id - b.id);
    return out;
  }

  /** Everything whose bounding circle overlaps the circle (x, y, radius). */
  queryCircle(x: number, y: number, radius: number): T[] {
    const candidates = this.queryRect(x - radius, y - radius, x + radius, y + radius);
    const out: T[] = [];
    for (const it of candidates) {
      const dx = it.x - x;
      const dy = it.y - y;
      const rr = radius + it.r;
      if (dx * dx + dy * dy <= rr * rr) out.push(it);
    }
    return out;
  }
}

function key(cx: number, cy: number): number {
  // Pack two signed 16-bit cell coordinates into one number.
  return ((cx & 0xffff) << 16) | (cy & 0xffff);
}
